import { useEffect, useState } from "react";
import { Box, HStack, Spinner, Text } from "@chakra-ui/react";
import { toaster } from "@/components/ui/toaster";

// Shared "Połącz z Google Sheets" card -- used by StepLeadSource (the lead source sheet) and StepOutput
// (each Google Sheets output integration's destination sheet). Owns its own check/connect state so
// multiple instances (e.g. several output integrations) don't interfere with each other.
// "Sprawdź dostęp" only probes access and stages the result in pendingColumns; nothing is applied until
// "Użyj tego arkusza" is clicked -- see plans/salesmanager-campaign-wizard/notes/task-20-notes.md.
const GoogleSheetsConnectPanel = ({ serviceAccountEmail, spreadsheetUrl, sheetName, isConnected, onCheckAccess, onConnected }) => {
    const [isChecking, setIsChecking] = useState(false);
    const [checkError, setCheckError] = useState(null);
    const [pendingColumns, setPendingColumns] = useState(null);

    // Editing the URL/tab invalidates any previous check -- it was only ever verified for the old value.
    useEffect(() => {
        setPendingColumns(null);
        setCheckError(null);
    }, [spreadsheetUrl, sheetName]);

    const handleCopyEmail = () => {
        if (!serviceAccountEmail) return;
        navigator.clipboard?.writeText(serviceAccountEmail);
        toaster.create({ type: "success", title: "Skopiowano adres e-mail" });
    };

    const handleCheckAccess = async () => {
        if (!spreadsheetUrl?.trim() || !sheetName?.trim()) {
            setCheckError("Podaj URL arkusza i nazwę zakładki.");
            return;
        }
        setIsChecking(true);
        setCheckError(null);
        setPendingColumns(null);
        try {
            const result = await onCheckAccess(spreadsheetUrl, sheetName);
            if (result?.isConnected) {
                setPendingColumns(result.columns ?? []);
            } else {
                setCheckError(result?.error ?? "Nie udało się zweryfikować dostępu.");
            }
        } finally {
            setIsChecking(false);
        }
    };

    const handleUseThisSheet = () => {
        if (!pendingColumns) return;
        onConnected?.(pendingColumns);
        setPendingColumns(null);
        toaster.create({ type: "success", title: "Arkusz podłączony — zapisz kampanię, żeby to zachować" });
    };

    return (
        <Box borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
            <Text fontSize="sm" color="text.white" fontWeight={600}>Połącz z Google Sheets</Text>
            <Text fontSize="xs" color="text.grey" mt={0.5}>
                Udostępnij arkusz jako Edytor na poniższy adres, potem sprawdź dostęp — bez logowania przez Google.
            </Text>
            <HStack mt={2} gap={2} align="center" flexWrap="wrap">
                <Box px={2} py={1} borderRadius={6} bg="bg.dark" borderWidth={1} borderColor="border.primary">
                    <Text fontSize="xs" color="text.white" fontFamily="mono">
                        {serviceAccountEmail ?? "…"}
                    </Text>
                </Box>
                <Box
                    as="button" type="button" onClick={handleCopyEmail} disabled={!serviceAccountEmail}
                    px={2} py={1} borderRadius={6} fontSize="xs"
                    bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                    opacity={serviceAccountEmail ? 1 : 0.5}
                >
                    Kopiuj
                </Box>
            </HStack>
            <HStack mt={3} justify="space-between" align="center" gap={4} flexWrap="wrap">
                <Text fontSize="xs" color={isConnected ? "green.400" : "text.grey"}>
                    Status: {isConnected ? "Połączono" : "Nie połączono"}
                </Text>
                <HStack gap={2} flexShrink={0}>
                    <Box
                        as="button" type="button" onClick={handleCheckAccess} disabled={isChecking}
                        px={4} py={2} borderRadius={8} fontSize="sm"
                        bg="bg.highlight" color="text.white" border="1px solid" borderColor="border.secondary"
                        opacity={isChecking ? 0.6 : 1}
                        display="flex" alignItems="center" gap={2}
                    >
                        {isChecking && <Spinner size="xs" />}
                        Sprawdź dostęp
                    </Box>
                    {pendingColumns && (
                        <Box
                            as="button" type="button" onClick={handleUseThisSheet}
                            px={4} py={2} borderRadius={8} fontSize="sm"
                            bg="purple.500" color="white"
                        >
                            Użyj tego arkusza
                        </Box>
                    )}
                </HStack>
            </HStack>
            {pendingColumns && (
                <Text fontSize="xs" color="green.400" mt={2}>
                    Dostęp działa, odczytano {pendingColumns.length} kolumn. Kliknij „Użyj tego arkusza”, żeby zastosować.
                </Text>
            )}
            {checkError && (
                <Text fontSize="xs" color="red.400" mt={2}>{checkError}</Text>
            )}
        </Box>
    );
};

export default GoogleSheetsConnectPanel;
